export type ParentReferrerData = {
  referrerUrl: string;
  referrerDomain?: string;
  queryParams: Record<string, string | string[]>;
};

/**
 * Helper to parse URL and extract query parameters
 * Returns null if URL parsing fails (invalid URL format)
 */
const parseUrlAndParams = (
  urlString: string
): {
  url: string;
  domain?: string;
  params: Record<string, string | string[]>;
} | null => {
  try {
    const url = new URL(urlString);
    const params: Record<string, string | string[]> = {};

    // Extract all query parameters
    url.searchParams.forEach((value, key) => {
      const existing = params[key];
      if (existing) {
        // Handle multiple values for same key
        if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          params[key] = [existing, value];
        }
      } else {
        params[key] = value;
      }
    });

    return {
      url: urlString,
      domain: url.hostname,
      params,
    };
  } catch (error) {
    // If URL parsing fails, return null to indicate invalid URL
    return null;
  }
};

/**
 * Captures the parent page URL when the scheduling app is embedded in an iframe.
 *
 * Uses a multi-layered fallback strategy to handle different embedding scenarios:
 * 1. First checks for 'parent_url' query parameter (explicitly passed by parent)
 *    - Works regardless of referrer policy restrictions
 *    - Recommended approach for customer integrations
 * 2. Falls back to document.referrer (browser-provided parent URL)
 *    - Works when referrer policy allows it
 *    - Backward compatible with existing implementations
 *
 * Also extracts query parameters from the parent URL for tracking.
 *
 * @returns Object containing referrer URL, domain, and query parameters
 */
export const getParentPageReferrer = (): ParentReferrerData | undefined => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return undefined;
  }

  // Layer 1: Check for explicitly passed parent_url parameter
  // This is the most reliable method as it works regardless of referrer policy
  try {
    const currentUrl = new URL(window.location.href);
    const parentUrlParam = currentUrl.searchParams.get('parent_url');

    if (parentUrlParam && parentUrlParam.trim() !== '') {
      const parsed = parseUrlAndParams(parentUrlParam);
      // Only use parsed parent_url if it's a valid URL
      if (parsed) {
        return {
          referrerUrl: parsed.url,
          referrerDomain: parsed.domain,
          queryParams: parsed.params,
        };
      }
      // If parsing failed, continue to fallback
    }
  } catch (error) {
    // Continue to fallback if parameter parsing fails
  }

  // Layer 2: Fall back to document.referrer
  // This works when the referrer policy allows it
  if (typeof document === 'undefined') {
    return undefined;
  }

  const referrer = document.referrer;

  // Return undefined if there's no referrer
  if (!referrer || referrer.trim() === '') {
    return undefined;
  }

  const parsed = parseUrlAndParams(referrer);

  // For document.referrer, return data even if parsing fails
  // We want to capture the raw referrer URL even if it's invalid
  if (parsed) {
    return {
      referrerUrl: parsed.url,
      referrerDomain: parsed.domain,
      queryParams: parsed.params,
    };
  } else {
    // Fallback for invalid referrer URL - still capture it
    return {
      referrerUrl: referrer,
      queryParams: {},
    };
  }
};
